// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { z } from 'zod';
import ConfigReader from './ConfigReader';
import { emptyToUndefined, assureIntOrUndefined } from './configUtils';

export enum Algorithm {
  RS256 = 'rs-256',
  HS256UNSAFE = 'hs-256-unsafe',
}

const hs256UnsafeSchema = z.object({
  algorithm: z.literal(Algorithm.HS256UNSAFE),
  secret: z.string(),
  audience: z.string(),
});
export type Hs256UnsafeAuth = z.infer<typeof hs256UnsafeSchema>;

export const rs256Schema = z.object({
  algorithm: z.literal(Algorithm.RS256),
  authority: z.string().url(),
  client_id: z.string(),
  audience: z.string(),
});
export type Rs256Auth = z.infer<typeof rs256Schema>;

const authSchema = z.discriminatedUnion('algorithm', [hs256UnsafeSchema, rs256Schema]);
export type AuthConf = z.infer<typeof authSchema>;

export enum Theme {
  DEFAULT = 'utilities',
  BROADRIDGE = 'broadridge',
}

const brandingSchema = z
  .object({
    app_name: z
      .string()
      .nullish()
      .transform((v) => (v === null || v === undefined || v === '' ? 'Utility' : v)),
    theme: z
      .union([z.enum(Theme), z.string()])
      .nullish()
      .transform((v) => (v === null || v === undefined || v === '' ? Theme.DEFAULT : (v as Theme))),
  })
  .optional()
  .default({
    app_name: 'Utility',
    theme: Theme.DEFAULT,
  });
export type BrandingConf = z.infer<typeof brandingSchema>;

const appSchema = z
  .object({
    mode: emptyToUndefined(z.enum(['utility']).optional().default('utility')),
    utility_backend_url: emptyToUndefined(z.string().optional().default('http://localhost:8080')),
    external_only: emptyToUndefined(z.boolean().optional().default(true)),
    deduplication_duration_in_seconds: emptyToUndefined(z.number().optional().default(30)),
    enableDappLogin: emptyToUndefined(
      z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .transform((val) => {
          return typeof val === 'boolean' ? val : val === 'true';
        })
        .optional()
        .default(false),
    ),
    query_limit: emptyToUndefined(z.number().optional().default(0)), // 0 means no limit (will be extracted from server)
  })
  .optional()
  .default({
    mode: 'utility',
    utility_backend_url: 'http://localhost:8080',
    external_only: true,
    deduplication_duration_in_seconds: 30,
    query_limit: 0,
    enableDappLogin: false,
  });

export type AppConf = z.infer<typeof appSchema>;

export const ACSDatabase = {
  NONE: 0,
  LRU: 1,
  IndexedDB: 2,
  OPFS: 3,
} as const;

const ACSDatabaseEnum = z.enum(ACSDatabase);
export type ACSDatabaseValue = z.infer<typeof ACSDatabaseEnum>;

const acsSchema = z
  .object({
    configVersion: assureIntOrUndefined(z.int().optional()).default(0),
    database: assureIntOrUndefined(ACSDatabaseEnum.optional().default(ACSDatabase.OPFS)),
    cacheLifespanMs: assureIntOrUndefined(
      z
        .number()
        .optional()
        .default(6 * 60 * 60 * 1000),
    ), // 6 hours,
    showLogsOption: emptyToUndefined(
      z.union([z.stringbool(), z.boolean()]).optional().default(true),
    ),
    enableLogsByDefault: emptyToUndefined(
      z.union([z.stringbool(), z.boolean()]).optional().default(false),
    ),
  })
  .optional()
  .default({
    configVersion: 0, // used for invalidating user settings stored in local storage (e.g. if we want to globally enable / disable acs logs, and we need to overwrite user settings)
    database: ACSDatabase.OPFS,
    cacheLifespanMs: 6 * 60 * 60 * 1000, // 6 hours,
    showLogsOption: true,
    enableLogsByDefault: false,
  });

const configSpec = z.strictObject({
  auth: authSchema,
  branding: brandingSchema,
  app: appSchema,
  acs: acsSchema,
});
export type UtilityConfig = z.infer<typeof configSpec>;

export const isHs256UnsafeAuthConfig = (auth: AuthConf): auth is Hs256UnsafeAuth => {
  return auth.algorithm === Algorithm.HS256UNSAFE;
};
export const isRs256AuthConfig = (auth: AuthConf): auth is Rs256Auth => {
  return auth.algorithm === Algorithm.RS256;
};

const configReader = new ConfigReader(configSpec);

export default configReader;
